import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clientService from '../services/clientService';
import { useNotification } from '../context/NotificationContext';

// RFC validation pattern for Mexican tax ID (persona física 13 chars, persona moral 12 chars)
const RFC_PATTERN = /^([A-ZÑ&]{3,4})(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])([A-Z\d]{3})$/;

function validateRFC(value) {
  if (!value || !value.trim()) return null;
  const cleanedRFC = value.trim().toUpperCase();
  return RFC_PATTERN.test(cleanedRFC);
}

/**
 * ClientForm
 * Reusable client create/edit form. Used both as a full page (ClientFormPage)
 * and inside a modal (CreateClientModal) so client creation can happen inline
 * from other flows (e.g. Client Quote) without duplicating validation logic.
 */
const ClientForm = ({ initialData = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isEditMode = !!initialData?.id;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    tax_id: initialData?.tax_id || '',
    phone: initialData?.phone || '',
    zip_code: initialData?.zip_code || '',
    address: initialData?.address || '',
    contacts: initialData?.contacts || []
  });

  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('clients.name') + ' ' + t('login.required');
    if (formData.tax_id.trim() && !validateRFC(formData.tax_id)) {
      newErrors.tax_id = t('clients.invalidRFC');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTaxIdBlur = () => {
    const cleaned = formData.tax_id.trim().toUpperCase();
    setFormData(prev => ({ ...prev, tax_id: cleaned }));

    if (cleaned && !validateRFC(cleaned)) {
      setErrors(prev => ({ ...prev, tax_id: t('clients.invalidRFC') }));
    } else {
      setErrors(prev => ({ ...prev, tax_id: undefined }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setNewContact(prev => ({ ...prev, [name]: value }));
  };

  const handleAddContact = () => {
    if (!(newContact.name.trim() || newContact.phone.trim() || newContact.email.trim())) {
      return;
    }

    if (editingIndex !== null) {
      setFormData(prev => ({
        ...prev,
        contacts: prev.contacts.map((c, i) => (i === editingIndex ? { ...newContact } : c))
      }));
      setEditingIndex(null);
    } else {
      setFormData(prev => ({
        ...prev,
        contacts: [...prev.contacts, { ...newContact }]
      }));
    }
    setNewContact({ name: '', phone: '', email: '' });
  };

  const handleEditContact = (index) => {
    setEditingIndex(index);
    setNewContact({ ...formData.contacts[index] });
  };

  const handleCancelEditContact = () => {
    setEditingIndex(null);
    setNewContact({ name: '', phone: '', email: '' });
  };

  const handleRemoveContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
    if (editingIndex === index) {
      handleCancelEditContact();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      let client;
      if (isEditMode) {
        const res = await clientService.updateClient(initialData.id, formData);
        client = res.data;
        notify.success(t('clients.clientUpdated'));
      } else {
        const res = await clientService.createClient(formData);
        client = res.data;
        notify.success(t('clients.clientCreated'));
      }
      onSuccess(client);
    } catch (err) {
      const errMsg = err.response?.data?.detail || (isEditMode ? t('common.failedToUpdate') : t('common.failedToCreate'));
      notify.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="form-fieldset">
        <legend>
          {t('clients.title')}
        </legend>

        <div className="form-group">
          <label className="form-label required">
            {t('clients.name')}
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('clients.name')}
            className={`form-input ${errors.name ? 'error' : ''}`}
          />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            {t('clients.taxId')}
          </label>
          <input
            type="text"
            name="tax_id"
            value={formData.tax_id}
            onChange={handleChange}
            onBlur={handleTaxIdBlur}
            placeholder={t('clients.taxId')}
            className={`form-input ${errors.tax_id ? 'error' : ''}`}
          />
          {errors.tax_id && <span className="form-error">{errors.tax_id}</span>}
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">
              {t('clients.phone')}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('clients.phone')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('clients.zipCode')}
            </label>
            <input
              type="text"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              placeholder={t('clients.zipCode')}
              className={`form-input ${errors.zip_code ? 'error' : ''}`}
            />
            {errors.zip_code && <span className="form-error">{errors.zip_code}</span>}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            {t('clients.address')}
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder={t('clients.address')}
            className={`form-input ${errors.address ? 'error' : ''}`}
          />
          {errors.address && <span className="form-error">{errors.address}</span>}
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>
          {t('clients.contacts')}
        </legend>

        {formData.contacts.length > 0 && (
          <div className="mb-20">
            {formData.contacts.map((contact, index) => (
              <div key={index} className="contact-card">
                <div className="contact-info">
                  <div className="contact-name">{contact.name}</div>
                  <div className="contact-detail">{contact.phone}</div>
                  <div className="contact-detail">{contact.email}</div>
                </div>
                <div className="contact-actions">
                  <button
                    type="button"
                    onClick={() => handleEditContact(index)}
                    className="btn btn-primary btn-small"
                  >
                    {t('clients.editContact')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveContact(index)}
                    className="btn btn-danger btn-small"
                  >
                    {t('clients.removeContact')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="add-contact-form">
          <h4>{editingIndex !== null ? t('clients.editContact') : t('clients.addContact')}</h4>

          <div className="form-group">
            <label className="form-label">
              {t('clients.contactName')}
            </label>
            <input
              type="text"
              name="name"
              value={newContact.name}
              onChange={handleContactChange}
              placeholder={t('clients.contactName')}
              className="form-input"
            />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">
                {t('clients.contactPhone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={newContact.phone}
                onChange={handleContactChange}
                placeholder={t('clients.contactPhone')}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {t('clients.contactEmail')}
              </label>
              <input
                type="email"
                name="email"
                value={newContact.email}
                onChange={handleContactChange}
                placeholder={t('clients.contactEmail')}
                className="form-input"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleAddContact}
            className="btn btn-primary"
          >
            {editingIndex !== null ? t('clients.saveContact') : t('clients.addContact')}
          </button>
          {editingIndex !== null && (
            <button
              type="button"
              onClick={handleCancelEditContact}
              className="btn btn-secondary"
            >
              {t('clients.cancelEditContact')}
            </button>
          )}
        </div>
      </fieldset>

      <div className="form-actions">
        <button
          type="submit"
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

export default ClientForm;
